import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { authApi, adminApi } from '../utils/api';

export const Login = ({ onLogin, onAdminLogin }) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Redirect if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const adminSecret = localStorage.getItem('adminSecret');
    
    if (token) {
      navigate('/dashboard');
    } else if (adminSecret) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error('Please enter email and password');
      return;
    }
    
    setIsLoading(true);
    
    // Smart detection: Check if this is likely an admin login
    const isLikelyAdmin = password.length >= 20 || 
                          email.toLowerCase().includes('admin@') ||
                          !email.includes('@');
    
    if (isLikelyAdmin) {
      // Try admin login first (using password as master secret)
      try {
        await adminApi.getStats(password);
        onAdminLogin(password);
        toast.success('Admin login successful');
        navigate('/admin/dashboard');
        return;
      } catch (adminError) {
        // If admin fails and email looks like user email, try user login
        if (email.includes('@') && !email.toLowerCase().includes('admin@')) {
          try {
            const response = await authApi.login(email, password);
            const { access_token, user } = response.data;
            
            localStorage.setItem('token', access_token);
            localStorage.setItem('user', JSON.stringify(user));
            
            onLogin(access_token, user);
            toast.success('Login successful');
            navigate('/dashboard');
            return;
          } catch (userError) {
            toast.error('Invalid email or password');
          }
        } else {
          toast.error('Invalid admin credentials');
        }
      }
    } else {
      // Try user login first
      try {
        const response = await authApi.login(email, password);
        const { access_token, user } = response.data;
        
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(user));
        
        onLogin(access_token, user);
        toast.success('Login successful');
        navigate('/dashboard');
        return;
      } catch (userError) {
        // If user login fails, try admin login with password as secret
        try {
          await adminApi.getStats(password);
          onAdminLogin(password);
          toast.success('Admin login successful');
          navigate('/admin/dashboard');
          return;
        } catch (adminError) {
          toast.error('Invalid email or password');
        }
      }
    }
    
    setIsLoading(false);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 flex items-center justify-center" data-testid="login-page">
      <div className="container mx-auto px-4 py-8 max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center mb-6">
            <img src="/logo.png" alt="Cangkir Wanesia" className="h-20 w-20 object-contain mb-3" />
            <span className="text-2xl font-bold text-gray-900">Cangkir Wanesia</span>
          </Link>
          <p className="text-gray-600">Logistics API Platform</p>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Login</CardTitle>
            <CardDescription>Access your account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="email-input"
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Password / Admin Secret"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full"
                disabled={isLoading}
                data-testid="login-button"
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>
              
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/v1/auth/google`;
                }}
                data-testid="google-login-button"
              >
                <svg className="mr-2 h-4 w-4" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512">
                  <path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path>
                </svg>
                Continue with Google
              </Button>
            </form>
          </CardContent>
        </Card>
        
        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};